import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { exchangeBingAdsCode, exchangeGoogleAdsCode, exchangeLinkedInOrganicCode } from '../../api/integrations';
import { exchangeGoogleAccountCode } from '../../api/settings';

interface OAuthState {
  provider: string;
  userId?: string;
  integrationId?: string;
  person?: string;
  exp?: number;
  nonce?: string;
  sig?: string;
}

function parseState(raw: string | null): OAuthState | null {
  if (!raw) return null;
  try {
    return JSON.parse(atob(raw)) as OAuthState;
  } catch {
    return null;
  }
}

export function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const exchangedRef = useRef(false);

  useEffect(() => {
    if (exchangedRef.current) return;

    const code = searchParams.get('code');
    const stateRaw = searchParams.get('state');
    const oauthError = searchParams.get('error');

    if (oauthError) {
      const description = searchParams.get('error_description');
      setError(`OAuth error: ${oauthError}${description ? ` (${description})` : ''}`);
      return;
    }

    if (!code) {
      setError('No authorization code received');
      return;
    }

    const state = parseState(stateRaw);
    if (!state?.provider) {
      setError('Invalid OAuth state');
      return;
    }

    if (!stateRaw) {
      setError('Missing OAuth state');
      return;
    }

    exchangedRef.current = true;

    async function exchange() {
      try {
        if (state!.provider === 'google_ads') {
          await exchangeGoogleAdsCode(code!, stateRaw!, state!.integrationId);
          navigate('/integrations', { replace: true });
        } else if (state!.provider === 'google_workspace') {
          await exchangeGoogleAccountCode(code!, stateRaw!);
          navigate('/settings', { replace: true });
        } else if (state!.provider === 'bing_ads') {
          if (!state!.integrationId) {
            setError('Missing integration ID in OAuth state');
            return;
          }
          await exchangeBingAdsCode(code!, stateRaw!, state!.integrationId);
          navigate('/integrations', { replace: true });
        } else if (state!.provider === 'linkedin_organic') {
          if (!state!.integrationId || !state!.person) {
            setError('Missing integration ID or person in OAuth state');
            return;
          }
          await exchangeLinkedInOrganicCode(code!, stateRaw!, state!.integrationId, state!.person);
          navigate('/integrations', { replace: true });
        } else {
          setError(`Unsupported provider: ${state!.provider}`);
          return;
        }
      } catch (err) {
        exchangedRef.current = false;
        setError(err instanceof Error ? err.message : 'Failed to complete OAuth flow');
      }
    }

    exchange();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="w-full max-w-md space-y-4 px-4">
          <Alert variant="error">{error}</Alert>
          <button
            onClick={() => navigate('/integrations', { replace: true })}
            className="text-sm text-secondary-600 hover:text-secondary-900 underline"
          >
            Back to Integrations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <Spinner size="lg" />
      <p className="text-sm text-secondary-600">Completing authorization...</p>
    </div>
  );
}
